import React, { useState, useEffect } from "react";
import {
  TextField,
  Button,
  FormControl,
  FormControlLabel,
  Switch,
  Typography,
  Grid,
  Divider,
  Box,
  InputLabel,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import Select from 'react-select';
import apiUrl from "../api";
import { format } from "date-fns";
import PopUpWindow from "../popUpWindow/PopUpWindow";

const ContractForm = ({ open, handleClose, contract = null, onSave, refreshContracts, readOnly = false }) => {
  const isEdit = Boolean(contract);
  const [formData, setFormData] = useState({
    title: "",
    property: "",
    start_date: new Date(),
    end_date: new Date(),
    charge: 0,
    charge_monthly: true,
    charge_daily: false,
    notes: "",
    active: true
  });
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const user = JSON.parse(localStorage.getItem("user"));

  // Fetch properties for dropdown using the dedicated getPropertiesNames endpoint
  useEffect(() => {
    const fetchProperties = async () => {
      try {
        const token = user?.token
        const response = await fetch(`${apiUrl}/property/getPropertiesNames`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        if (response.ok) {
          const data = await response.json();
          setProperties(data.propertiesNames.map(property => ({
            value: property._id,
            label: property.name
          })));
        } else {
          console.error("Failed to fetch properties");
        }
      } catch (error) {
        console.error("Error fetching properties:", error);
      }
    };

    fetchProperties();
  }, []);

  // Set form data if editing an existing contract
  useEffect(() => {
    if (contract) {
      setFormData({
        title: contract.title,
        property: contract.property._id,
        start_date: new Date(contract.start_date),
        end_date: new Date(contract.end_date),
        charge: contract.charge,
        charge_monthly: contract.charge_monthly,
        charge_daily: contract.charge_daily,
        notes: contract.notes || "",
        active: contract.active
      });
    }
  }, [contract]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // Special handling for charge switches
    if (name === 'charge_monthly' || name === 'charge_daily') {
      // If trying to turn off the last active switch, prevent it
      if (!checked && 
          ((name === 'charge_monthly' && !formData.charge_daily) || 
           (name === 'charge_daily' && !formData.charge_monthly))) {
        return;
      }
      
      // If turning on one switch, turn off the other
      if (checked) {
        setFormData({
          ...formData,
          charge_monthly: name === 'charge_monthly',
          charge_daily: name === 'charge_daily'
        });
        return;
      }
    }

    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const handleSelectChange = (selectedOption, { name }) => {
    setFormData({
      ...formData,
      [name]: selectedOption?.value,
    });
  };

  const handleDateChange = (name, value) => {
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError("");
    try {
      const token = user?.token;
      const method = isEdit ? "PUT" : "POST";
      const url = isEdit
        ? `${apiUrl}/contract/${contract._id}`
        : `${apiUrl}/contract`;

      // Format dates for API
      const apiFormData = {
        ...formData,
        start_date: format(formData.start_date, "yyyy-MM-dd'T'HH:mm:ss.SSSxxx"),
        end_date: format(formData.end_date, "yyyy-MM-dd'T'HH:mm:ss.SSSxxx")
      };

      const response = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(apiFormData),
      });

      const data = await response.json();
      
      if (response.ok) {
        if (onSave) onSave(data.contract);
        if (refreshContracts) refreshContracts();
        handleClose();
      } else {
        setError(data.message || "Failed to save contract");
      }
    } catch (err) {
      setError("An error occurred while saving the contract");
      console.error("Contract save error:", err);
    } finally {
      setLoading(false);
    }
  };

  // Get the currently selected property option
  const selectedPropertyOption = properties.find(option => option.value === formData.property) || null;
  
  // Custom styles for react-select
  const selectStyles = {
    control: (base, state) => ({
      ...base,
      minHeight: '56px',
      borderColor: '#c4c4c4',
      boxShadow: state.isFocused ? '0 0 0 1px #1976d2' : null,
      '&:hover': {
        borderColor: state.isFocused ? '#1976d2' : '#c4c4c4',
      },
    }),
    menu: base => ({
      ...base,
      zIndex: 10001,
      position: 'absolute',
      width: '100%',
    }),
    menuPortal: base => ({
      ...base,
      zIndex: 10001,
    }),
    // Adding additional style for dropdown container
    container: base => ({
      ...base,
      width: '100%',
      position: 'relative',
    }),
  };

  // Custom styles for date pickers to ensure they appear above other elements
  const datePickerSx = {
    '& .MuiPopover-root, & .MuiPaper-root, & .MuiDialog-root, & .MuiModal-root, & .MuiPopper-root': {
      zIndex: 1000001 // Set z-index for date picker popup
    }
  };

  if (!open) return null;

  return (
    <PopUpWindow 
      title={readOnly ? "View Contract Details" : isEdit ? "Edit Contract" : "Create New Contract"}
      onClose={handleClose}
      width="80%"
      color="#ffffff"
    >
      <Box sx={{ p: 2 }}>
        {error && (
          <Box my={2}>
            <Typography color="error">{error}</Typography>
          </Box>
        )}
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              name="title"
              label="Contract Title"
              fullWidth
              value={formData.title}
              onChange={handleChange}
              required
              disabled={readOnly}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Box sx={{ mb: 1 }}>
  
              <Select
                id="property-select"
                name="property"
                value={selectedPropertyOption}
                onChange={handleSelectChange}
                options={properties}
                isDisabled={readOnly}
                styles={selectStyles}
                placeholder="Select Property"
                menuPlacement="auto"
                menuPosition="fixed"
                menuPortalTarget={document.body}
                classNamePrefix="select"
                isClearable={!readOnly}
                
              />
            </Box>
          </Grid>

          <Grid item xs={12} sm={6}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Start Date"
                value={formData.start_date}
                onChange={(newValue) => handleDateChange("start_date", newValue)}
                disabled={readOnly}
                slotProps={{
                  popper: {
                    style: { zIndex: 1000001 },
                    sx: datePickerSx
                  },
                  textField: {
                    fullWidth: true
                  }
                }}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} sm={6}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="End Date"
                value={formData.end_date}
                onChange={(newValue) => handleDateChange("end_date", newValue)}
                disabled={readOnly}
                slotProps={{
                  popper: {
                    style: { zIndex: 1000001 },
                    sx: datePickerSx
                  },
                  textField: {
                    fullWidth: true
                  }
                }}
              />
            </LocalizationProvider>
          </Grid>

          <Grid item xs={12}>
            <Divider />
            <Typography variant="subtitle1" mt={2} mb={1}>
              Pricing Details
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <TextField
              name="charge"
              label="Charge Amount"
              type="number"
              fullWidth
              value={formData.charge}
              onChange={handleChange}
              disabled={readOnly}
              InputProps={{
                startAdornment: <Typography sx={{ mr: 1 }}>€</Typography>,
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={
                <Switch
                  name="charge_monthly"
                  checked={formData.charge_monthly}
                  onChange={handleChange}
                  disabled={readOnly}
                />
              }
              label="Monthly Charge"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={
                <Switch
                  name="charge_daily"
                  checked={formData.charge_daily}
                  onChange={handleChange}
                  disabled={readOnly}
                />
              }
              label="Daily Charge"
            />
          </Grid>

          <Grid item xs={12}>
            <Divider />
            <Typography variant="subtitle1" mt={2} mb={1}>
              Additional Information
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <TextField
              name="notes"
              label="Notes"
              multiline
              rows={4}
              fullWidth
              value={formData.notes}
              onChange={handleChange}
              disabled={readOnly}
            />
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  name="active"
                  checked={formData.active}
                  onChange={handleChange}
                  disabled={readOnly}
                />
              }
              label="Contract Active"
            />
          </Grid>
        </Grid>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
          <Button onClick={handleClose} sx={{ mr: 1 }}>
            {readOnly ? "Close" : "Cancel"}
          </Button>
          {!readOnly && (
            <Button 
              onClick={handleSubmit} 
              variant="contained" 
              color="primary" 
              disabled={loading}
            >
              {loading ? "Saving..." : isEdit ? "Update" : "Create"}
            </Button>
          )}
        </Box>
      </Box>
    </PopUpWindow>
  );
};

export default ContractForm;